import { FC, useState, useMemo, useEffect, useRef } from "react";
import { Button, Divider, Slider } from "../../../../components";
import { useLoanApplication, useLoanCalculator } from "../../../../contexts";
import { useFormik } from "formik";
import {
  getReceivablesLOCLoanValidationSchema,
  LINE_OF_CREDIT_INITIAL_VALUES,
} from "../../service";
import { useStepper } from "../../../LoanApplicationSteps/Stepper";
import { ArrowLeftIcon } from "../../../../assets/icons";
import {
  LoanApplicationStage,
  LoanProductType,
} from "../../../../hooks/api/types";
import { Tags as LoanCalculatorTags } from "../../../../components/Tags";
import { MessageBar as LoanCalculatorMessage } from "../../../../components/MessageBar";
import TermsAndConditionsSection from "../../TermsAndConditionsSection";
import { useDpConfig } from "../../../../contexts/dpConfig";
import { LoanAmountInput } from "../../LoanAmountInput";

type Props = {
  editMode?: boolean;
  tags?: string[];
  message?: string;
  handleSubmit?: () => void;
  handleCancel?: () => void;
};

const FactoringLOCLoanCalculator: FC<Props> = ({
  editMode = false,
  tags = [],
  message = "",
  handleSubmit: handleSubmitFromParent,
  handleCancel: handleCancelFromParent,
}) => {
  const [isPartnerTermsAccepted, setIsPartnerTermsAccepted] =
    useState<boolean>(false);
  const [submissionError, setSubmissionError] = useState<boolean>(false);

  const loanAmountInputRef = useRef<HTMLInputElement>(null);
  const {
    state: { factoringCreditLine, constraints },
    actions: { setFactoringCreditLine },
  } = useLoanCalculator();

  const {
    state: { sourceId, locFactoringLoan, stage },
    actions: { update },
  } = useLoanApplication();

  const {
    state: { dpTermsUrl, uiLocalizationData, products },
  } = useDpConfig();

  const { nextStep } = useStepper();

  const {
    values,
    setFieldValue,
    handleSubmit,
    errors,
    isSubmitting,
    setSubmitting,
  } = useFormik({
    enableReinitialize: true,
    initialValues: {
      ...LINE_OF_CREDIT_INITIAL_VALUES,
      loanAmount:
        editMode && locFactoringLoan?.amount
          ? locFactoringLoan.amount
          : constraints.factoringCreditLine?.amount?.initial ||
            LINE_OF_CREDIT_INITIAL_VALUES.loanAmount,
    },
    validationSchema: getReceivablesLOCLoanValidationSchema({
      loanAmount: {
        min: constraints.factoringCreditLine.amount.min,
        max: constraints.factoringCreditLine.amount.max,
        unit: constraints.factoringCreditLine?.amount?.unit ?? "AED",
      },
    }),
    onSubmit: async (values) => {
      setSubmitting(true);
      if (!isPartnerTermsAccepted && dpTermsUrl) {
        setSubmissionError(true);
        setSubmitting(false);
        return;
      }
      let nextStage = LoanApplicationStage.OWNER_INFORMATION;
      if (sourceId) {
        nextStage = LoanApplicationStage.DOCUMENTS_UPLOAD;
      }
      await update({
        loanType: LoanProductType.LOC_FACTORING,
        stage: nextStage,
        locFactoringLoan: {
          ...locFactoringLoan,
          amount: values.loanAmount,
        },
      });
      setFactoringCreditLine({
        amount: values.loanAmount,
      });
      nextStep();
      setSubmitting(false);
    },
  });

  const refocusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refocusInput = () => {
    const el = loanAmountInputRef.current;
    if (el) {
      el.focus({ preventScroll: true });
      const len = el.value.length;
      el.setSelectionRange(len, len);
    }
  };

  const handleLoanAmountSliderChange = (value: number) => {
    setFieldValue("loanAmount", value);
    setFactoringCreditLine({
      amount: value,
    });
    if (refocusTimeoutRef.current) clearTimeout(refocusTimeoutRef.current);
    refocusTimeoutRef.current = setTimeout(refocusInput, 350);
  };

  const handleTermsChange = (checked: boolean) => {
    setIsPartnerTermsAccepted(checked);
    if (checked) {
      setSubmissionError(false);
    }
  };

  const productConfig = useMemo(() => {
    return products?.find(
      (product: any) => product.type === LoanProductType.LOC_FACTORING
    );
  }, [products]);

  const productTitle = useMemo(() => {
    return (
      productConfig?.config?.customProductAlias || productConfig?.displayName
    );
  }, [productConfig]);

  const productDescription = useMemo(() => {
    return uiLocalizationData?.loanCalculatorScreen?.subtitle;
  }, [uiLocalizationData]);

  useEffect(() => {
    return () => {
      if (refocusTimeoutRef.current) clearTimeout(refocusTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    // Capture the state *as it is* when the component mounts in edit mode
    // Ensure a deep copy if factoringCreditLine contains objects that might be mutated elsewhere
    const initialState =
      editMode && factoringCreditLine?.amount
        ? JSON.parse(JSON.stringify(factoringCreditLine)) // Use deep copy
        : null;

    return () => {
      if (editMode && initialState) {
        setFactoringCreditLine(initialState);
      }
    };
  }, []);

  return (
    <div className="cx-w-full cx-flex cx-flex-col cx-p-10 cx-gap-10 cx-items-center cx-rounded-modal cx-border-0.5 cx-border-[#DDD] cx-shadow-modal cx-background-calculator-gradient">
      <div className="cx-flex cx-flex-col cx-items-center cx-text-center cx-w-full">
        <div className="cx-flex cx-items-center cx-gap-2 cx-mb-1">
          <span className="cx-inline-block cx-h-2 cx-w-2 cx-rounded-full cx-bg-teal-500"></span>
          <div className="cx-text-sm cx-text-text-secondary">
            Line of credit
          </div>
        </div>
        <div className="cx-font-bold cx-text-2xl">
          {productTitle || "Factoring"}
        </div>
        {productDescription && (
          <div className="cx-text-sm cx-text-text-secondary cx-mt-1 cx-max-w-[560px]">
            {productDescription}
          </div>
        )}
        <div className="cx-text-sm cx-text-text-tertiary cx-mt-1 cx-max-w-[560px]">
          Customize loan to your specific financial needs and repayment
          preference
        </div>
        <LoanCalculatorTags tags={tags} />
      </div>

      <div className="cx-flex cx-flex-col cx-items-center cx-w-full -cx-mt-6">
        <LoanAmountInput
          value={values.loanAmount}
          onValueChange={handleLoanAmountSliderChange}
          inputRef={loanAmountInputRef}
        />
      </div>

      <div className="cx-flex cx-flex-row cx-w-full cx-items-center cx-justify-center">
        <div className="cx-flex cx-flex-col cx-w-full cx-max-w-[560px]">
          <Slider
            initial={values.loanAmount}
            min={constraints.factoringCreditLine?.amount?.min}
            max={constraints.factoringCreditLine?.amount?.max}
            unit={constraints.factoringCreditLine?.amount?.unit}
            value={values.loanAmount}
            label=""
            onChange={(value) => handleLoanAmountSliderChange(value)}
            renderThumb={false}
            interval={constraints.factoringCreditLine?.amount?.interval}
          />
          {errors.loanAmount && (
            <div className="cx-text-xs cx-text-text-error">
              {errors.loanAmount}
            </div>
          )}
        </div>
      </div>
      {stage !== LoanApplicationStage.REVIEW && (
        <div className="cx-w-full">
          <TermsAndConditionsSection
            onTermsChange={handleTermsChange}
            loanType={LoanProductType.LOC_FACTORING}
          />
          {submissionError && (
            <div className="cx-w-full cx-bg-error-lighter cx-rounded-md cx-mt-4 cx-p-4">
              <div className="cx-text-sm cx-text-text-error">
                Please accept the Terms and Conditions to begin the application
              </div>
            </div>
          )}
        </div>
      )}
      <div className="md:cx-w-[400px] cx-w-[263px]">
        {editMode ? (
          <div className="cx-w-full cx-flex cx-flex-row cx-gap-x-4">
            <div className="cx-w-1/4">
              <Button
                label={<ArrowLeftIcon />}
                type="button"
                onClick={handleCancelFromParent}
                fullWidth
                outlined
              />
            </div>
            <div className="cx-w-3/4">
              <Button
                type="button"
                label="Confirm"
                fullWidth
                onClick={() => {
                  setSubmitting(true);
                  handleSubmitFromParent?.();
                }}
                disabled={Object.keys(errors).length > 0 || isSubmitting}
              />
            </div>
          </div>
        ) : (
          <Button
            label="Continue"
            arrow="right"
            fullWidth
            onClick={handleSubmit}
            disabled={Object.keys(errors).length > 0 || isSubmitting}
          />
        )}
      </div>
      <Divider />

      <div className="cx-block">
        {uiLocalizationData?.loanCalculatorScreen?.disclaimer && (
          <p className="cx-font-normal cx-text-sm cx-text-text-secondary cx-text-center cx-mt-1">
            {uiLocalizationData?.loanCalculatorScreen?.disclaimer}
          </p>
        )}
      </div>
      <LoanCalculatorMessage message={message} />
    </div>
  );
};

export default FactoringLOCLoanCalculator;
